import React from 'react';

// Cadet Pilot is the renamed AeroPace. The App Store privacy link
// (onyxup.net/aeropace/privacy) is already registered and has always
// rendered what lives in the historical AeroPaceTerms.js file content below.
// /aeropace/terms has always rendered the historical AeroPacePrivacy.js
// content. This mapping is preserved on purpose — do not "fix" it.

export const updated = '17 April 2026';

// Renders at /app/cadet-pilot/privacy and /aeropace/privacy
// (sourced from today's AeroPaceTerms.js — the EULA/Terms body)
const privacyContentEn = (
  <>
    <h2>End User License Agreement (EULA)</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <h2>1. License Grant</h2>
    <p>
      OnyxUp grants you a limited, non-exclusive, non-transferable, and revocable license to download, install, and use
      the Cadet Pilot: Aptitude Test mobile application ("App") on a device that you own or control, strictly in
      accordance with this Agreement.
    </p>

    <h2>2. License Restrictions</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Reverse engineer, decompile, disassemble, or attempt to extract source code except where mandatory law allows</li>
      <li>Modify, adapt, translate, or create derivative works of the App without permission</li>
      <li>Sell, sublicense, rent, lease, or distribute the App to third parties</li>
      <li>Use the App for unlawful purposes or to interfere with the integrity or performance of our systems</li>
      <li>Bypass or attempt to bypass security or access controls</li>
    </ul>

    <h2>3. User Responsibilities</h2>
    <p>You are solely responsible for:</p>
    <ul>
      <li>Using the App in compliance with aviation authorities, employers, schools, and applicable laws in your jurisdiction</li>
      <li>Ensuring that any practice you perform is safe for your environment (e.g. not while operating vehicles or aircraft)</li>
      <li>Maintaining the confidentiality of your account credentials, if you use account features</li>
    </ul>

    <h2>4. Training and Simulation Disclaimer</h2>
    <p>
      The App provides practice, training, and simulation-style experiences for psychomotor and related skills. It is
      intended for preparation and self-assessment only. It does not constitute an official aviation examination,
      certification, medical fitness determination, or regulatory approval. Results in the App do not guarantee success
      on any real-world test.
    </p>

    <h2>5. No Professional Advice</h2>
    <p>
      Nothing in the App is medical, psychological, legal, or flight instruction. For medical clearance, licensing, or
      career decisions, consult qualified professionals and your regulator's published materials.
    </p>

    <h2>6. Intellectual Property</h2>
    <p>
      The App, including content, features, software, designs, text, graphics, and trademarks, is the property of
      OnyxUp or its licensors and is protected by applicable intellectual property laws. Unauthorized use is prohibited.
    </p>

    <h2>7. Privacy and Data Usage</h2>
    <p>
      Your use of the App is also governed by our Privacy Policy. By using the App, you acknowledge the collection and
      processing of information as described there.
    </p>

    <h2>8. Disclaimers</h2>
    <p>
      THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
      INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT, TO THE
      FULLEST EXTENT PERMITTED BY LAW.
    </p>

    <h2>9. Limitation of Liability</h2>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, ONYXUP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
      CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, PROFITS, OR OPPORTUNITIES, ARISING FROM OR RELATED TO
      YOUR USE OF OR INABILITY TO USE THE APP.
    </p>

    <h2>10. Termination</h2>
    <p>
      This Agreement remains effective until terminated. OnyxUp may suspend or terminate your access if you violate
      these terms. Upon termination, you must cease use and delete copies of the App as applicable.
    </p>

    <h2>11. Updates and Changes</h2>
    <p>
      OnyxUp may update the App or modify this Agreement. Continued use after changes become effective constitutes
      acceptance of the revised terms where permitted by law.
    </p>

    <h2>12. Governing Law</h2>
    <p>
      This Agreement shall be governed by and construed in accordance with the laws of the Republic of Turkey, without
      regard to conflict-of-law principles. Disputes shall be subject to the exclusive jurisdiction of Turkish courts,
      except where mandatory consumer protection law in your country provides otherwise.
    </p>

    <h2>13. Contact Information</h2>
    <p>If you have questions about this Agreement, please contact us:</p>
    <p>📧 <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

// Renders at /app/cadet-pilot/terms and /aeropace/terms
// (sourced from today's AeroPacePrivacy.js — the Privacy Policy body)
const termsContentEn = (
  <>
    <h2>Privacy Policy</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <h2>1. Introduction and Scope</h2>
    <p>
      Cadet Pilot: Aptitude Test ("we", "our", "us") is published by OnyxUp. This Privacy Policy explains how we
      collect, use, store, and delete information when you use the Cadet Pilot mobile application ("App"). The App
      offers training and simulation-style exercises related to aviation test preparation and psychomotor skills.
    </p>

    <h2>2. User Eligibility</h2>
    <p>
      The App is intended for users who meet the minimum age required by the app store through which you download it,
      and who are legally able to enter into this Policy in their jurisdiction. If you are under that age, do not use
      the App.
    </p>

    <h2>3. Information We Collect</h2>
    <p><strong>Account and profile (if applicable):</strong></p>
    <ul>
      <li>If you create an account, we may collect identifiers such as email, username, or authentication tokens provided by you or a sign-in provider.</li>
      <li>We use this information to operate your account, sync progress where offered, and communicate important service updates.</li>
    </ul>

    <p><strong>App activity and performance data:</strong></p>
    <ul>
      <li>We may collect data about how you use the App (e.g. session duration, feature usage, scores or completion states for exercises) to improve stability and content.</li>
      <li>Depending on your settings and our implementation, some of this may be processed in aggregated or pseudonymous form.</li>
    </ul>

    <p><strong>Technical data:</strong></p>
    <ul>
      <li>Device model, operating system version, app version, language, and diagnostic logs may be collected to fix crashes and improve compatibility.</li>
      <li>We do not use the App to diagnose medical conditions; the App is not a medical device.</li>
    </ul>

    <p><strong>Health and special categories:</strong></p>
    <ul>
      <li>We do not require you to provide health data to use core training features. If optional features ever collect sensitive categories of data, we will describe them separately and rely on a valid legal basis where required.</li>
    </ul>

    <h2>4. Third-Party Services</h2>
    <p>
      We may use trusted service providers (e.g. hosting, analytics, crash reporting, authentication, or app stores)
      that process data on our behalf. Their use is governed by their own terms and privacy notices where they act as
      independent controllers (for example, Apple or Google as app store operators). We use encrypted transport (such
      as HTTPS) where appropriate for data in transit.
    </p>

    <h2>5. How We Use Your Information</h2>
    <p>
      We use collected information to provide and improve the App, personalize your experience where applicable,
      ensure security, comply with legal obligations, and communicate with you about the service. We do not sell your
      personal information.
    </p>

    <h2>6. Retention and Deletion</h2>
    <p>
      We retain information only as long as needed for the purposes above or as required by law. You may request
      deletion of personal data associated with your account (where applicable) by contacting us or using in-app
      controls if available.
    </p>

    <h2>7. Your Rights</h2>
    <p>
      Depending on your location, you may have rights to access, correct, delete, or restrict processing of your
      personal data, or to object to certain processing. Contact us to exercise these rights; we will respond within a
      reasonable timeframe.
    </p>

    <h2>8. Security</h2>
    <p>
      We implement reasonable technical and organizational measures to protect data. No method of transmission or
      storage is completely secure; we cannot guarantee absolute security.
    </p>

    <h2>9. International Transfers</h2>
    <p>
      If we or our processors transfer data across borders, we take steps consistent with applicable law (such as
      appropriate safeguards or contractual clauses) where required.
    </p>

    <h2>10. Changes to this Policy</h2>
    <p>
      We may update this Policy from time to time. Material changes will be reflected by an updated "Last Updated"
      date and, where appropriate, additional notice in the App or on this page.
    </p>

    <h2>11. Contact</h2>
    <p>For privacy questions or data requests, contact:</p>
    <p>📧 <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

const privacyContentTr = (
  <>
    <h2>Son Kullanıcı Lisans Sözleşmesi (EULA)</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <h2>1. Lisans Verilmesi</h2>
    <p>
      OnyxUp, sahip olduğunuz veya kontrol ettiğiniz bir cihazda, Cadet Pilot: Aptitude Test mobil uygulamasını
      ("Uygulama") bu Sözleşmeye sıkı sıkıya bağlı kalarak indirmeniz, kurmanız ve kullanmanız için size sınırlı,
      münhasır olmayan, devredilemez ve iptal edilebilir bir lisans verir.
    </p>

    <h2>2. Lisans Kısıtlamaları</h2>
    <p>Aşağıdakileri yapmamayı kabul edersiniz:</p>
    <ul>
      <li>Zorunlu yasaların izin verdiği durumlar dışında ters mühendislik yapmak, derlemeyi geri almak, parçalara ayırmak veya kaynak kodu çıkarmaya çalışmak</li>
      <li>İzin almadan Uygulamayı değiştirmek, uyarlamak, çevirmek veya türev çalışmalar oluşturmak</li>
      <li>Uygulamayı üçüncü taraflara satmak, alt lisanslamak, kiralamak veya dağıtmak</li>
      <li>Uygulamayı yasa dışı amaçlarla kullanmak veya sistemlerimizin bütünlüğüne ya da performansına müdahale etmek</li>
      <li>Güvenlik veya erişim kontrollerini aşmaya çalışmak</li>
    </ul>

    <h2>3. Kullanıcı Sorumlulukları</h2>
    <p>Aşağıdakilerden yalnızca siz sorumlusunuz:</p>
    <ul>
      <li>Uygulamayı havacılık otoriteleri, işverenler, okullar ve kendi bulunduğunuz yargı alanındaki geçerli yasalara uygun şekilde kullanmak</li>
      <li>Yaptığınız herhangi bir pratiğin bulunduğunuz ortam için güvenli olduğundan emin olmak (örn. araç veya hava aracı kullanırken değil)</li>
      <li>Hesap özelliklerini kullanıyorsanız hesap kimlik bilgilerinizin gizliliğini korumak</li>
    </ul>

    <h2>4. Eğitim ve Simülasyon Feragatnamesi</h2>
    <p>
      Uygulama, psikomotor ve ilgili beceriler için pratik, eğitim ve simülasyon tarzı deneyimler sunar. Yalnızca
      hazırlık ve kendi kendini değerlendirme amaçlıdır. Resmi bir havacılık sınavı, sertifikasyon, tıbbi uygunluk
      tespiti veya düzenleyici onay niteliği taşımaz. Uygulamadaki sonuçlar gerçek bir testte başarıyı garanti etmez.
    </p>

    <h2>5. Profesyonel Tavsiye Değildir</h2>
    <p>
      Uygulamadaki hiçbir şey tıbbi, psikolojik, hukuki veya uçuş eğitimi niteliğinde değildir. Tıbbi onay, lisanslama
      veya kariyer kararları için nitelikli profesyonellere ve düzenleyicinizin yayınladığı materyallere başvurun.
    </p>

    <h2>6. Fikri Mülkiyet</h2>
    <p>
      İçerik, özellikler, yazılım, tasarımlar, metin, grafikler ve ticari markalar dahil olmak üzere Uygulama,
      OnyxUp'ın veya lisans verenlerinin mülkiyetindedir ve geçerli fikri mülkiyet yasalarıyla korunur. Yetkisiz
      kullanım yasaktır.
    </p>

    <h2>7. Gizlilik ve Veri Kullanımı</h2>
    <p>
      Uygulamayı kullanımınız ayrıca Gizlilik Politikamıza da tabidir. Uygulamayı kullanarak, orada açıklanan
      bilgi toplama ve işleme faaliyetlerini kabul etmiş olursunuz.
    </p>

    <h2>8. Feragatnameler</h2>
    <p>
      UYGULAMA, YASALARIN İZİN VERDİĞİ AZAMİ ÖLÇÜDE, SATILABİLİRLİK, BELİRLİ BİR AMACA UYGUNLUK VE İHLAL ETMEME
      DAHİL OLMAK ÜZERE AÇIK VEYA ZIMNİ HİÇBİR GARANTİ OLMAKSIZIN "OLDUĞU GİBİ" VE "MEVCUT OLDUĞU ŞEKİLDE"
      SAĞLANMAKTADIR.
    </p>

    <h2>9. Sorumluluğun Sınırlandırılması</h2>
    <p>
      YASALARIN İZİN VERDİĞİ AZAMİ ÖLÇÜDE, ONYXUP; UYGULAMAYI KULLANIMINIZDAN VEYA KULLANAMAMANIZDAN KAYNAKLANAN
      HİÇBİR DOLAYLI, ARIZİ, ÖZEL, SONUÇ OLARAK ORTAYA ÇIKAN VEYA CEZAİ ZARARDAN YA DA VERİ, KÂR VEYA FIRSAT
      KAYBINDAN SORUMLU TUTULAMAZ.
    </p>

    <h2>10. Fesih</h2>
    <p>
      Bu Sözleşme, feshedilene kadar geçerliliğini sürdürür. Bu şartları ihlal etmeniz durumunda OnyxUp erişiminizi
      askıya alabilir veya sonlandırabilir. Fesih üzerine, kullanımı durdurmalı ve geçerliyse Uygulamanın
      kopyalarını silmelisiniz.
    </p>

    <h2>11. Güncellemeler ve Değişiklikler</h2>
    <p>
      OnyxUp, Uygulamayı güncelleyebilir veya bu Sözleşmeyi değiştirebilir. Değişiklikler yürürlüğe girdikten sonra
      kullanımın sürdürülmesi, yasaların izin verdiği ölçüde revize edilmiş şartların kabulü anlamına gelir.
    </p>

    <h2>12. Geçerli Hukuk</h2>
    <p>
      Bu Sözleşme, kanunlar ihtilafı kurallarına bakılmaksızın Türkiye Cumhuriyeti yasalarına göre yönetilir ve
      yorumlanır. Anlaşmazlıklar, ülkenizdeki zorunlu tüketici koruma yasasının aksini öngördüğü durumlar hariç,
      Türk mahkemelerinin münhasır yargı yetkisine tabidir.
    </p>

    <h2>13. İletişim Bilgileri</h2>
    <p>Bu Sözleşme hakkında sorularınız varsa bizimle iletişime geçin:</p>
    <p>📧 <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

const termsContentTr = (
  <>
    <h2>Gizlilik Politikası</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <h2>1. Giriş ve Kapsam</h2>
    <p>
      Cadet Pilot: Aptitude Test ("biz", "bizim"), OnyxUp tarafından yayınlanmaktadır. Bu Gizlilik Politikası, Cadet
      Pilot mobil uygulamasını ("Uygulama") kullandığınızda bilgileri nasıl topladığımızı, kullandığımızı,
      sakladığımızı ve sildiğimizi açıklar. Uygulama, havacılık test hazırlığı ve psikomotor becerilerle ilgili
      eğitim ve simülasyon tarzı alıştırmalar sunar.
    </p>

    <h2>2. Kullanıcı Uygunluğu</h2>
    <p>
      Uygulama, indirdiğiniz uygulama mağazasının gerektirdiği minimum yaş şartını karşılayan ve bulunduğu yargı
      alanında bu Politikaya yasal olarak taraf olabilecek kullanıcılar için tasarlanmıştır. Bu yaşın altındaysanız
      Uygulamayı kullanmayın.
    </p>

    <h2>3. Topladığımız Bilgiler</h2>
    <p><strong>Hesap ve profil (varsa):</strong></p>
    <ul>
      <li>Bir hesap oluşturursanız, sizin veya bir oturum açma sağlayıcısının sunduğu e-posta, kullanıcı adı veya kimlik doğrulama belirteçleri gibi tanımlayıcıları toplayabiliriz.</li>
      <li>Bu bilgileri hesabınızı işletmek, sunulduğunda ilerlemenizi senkronize etmek ve önemli hizmet güncellemelerini iletmek için kullanırız.</li>
    </ul>

    <p><strong>Uygulama etkinliği ve performans verileri:</strong></p>
    <ul>
      <li>Kararlılığı ve içeriği geliştirmek için Uygulamayı nasıl kullandığınıza ilişkin verileri (örn. oturum süresi, özellik kullanımı, alıştırmalar için puanlar veya tamamlanma durumları) toplayabiliriz.</li>
      <li>Ayarlarınıza ve uygulamamıza bağlı olarak, bunların bir kısmı toplu veya takma adlı biçimde işlenebilir.</li>
    </ul>

    <p><strong>Teknik veriler:</strong></p>
    <ul>
      <li>Çökmeleri düzeltmek ve uyumluluğu artırmak için cihaz modeli, işletim sistemi sürümü, uygulama sürümü, dil ve tanılama kayıtları toplanabilir.</li>
      <li>Uygulamayı tıbbi durumları teşhis etmek için kullanmayız; Uygulama bir tıbbi cihaz değildir.</li>
    </ul>

    <p><strong>Sağlık ve özel kategoriler:</strong></p>
    <ul>
      <li>Temel eğitim özelliklerini kullanmak için sağlık verisi sağlamanızı gerektirmeyiz. İsteğe bağlı özellikler hassas veri kategorileri toplarsa, bunları ayrıca açıklar ve gerektiğinde geçerli bir yasal dayanağa güveniriz.</li>
    </ul>

    <h2>4. Üçüncü Taraf Hizmetleri</h2>
    <p>
      Bizim adımıza veri işleyen güvenilir hizmet sağlayıcıları (örn. barındırma, analitik, çökme raporlama, kimlik
      doğrulama veya uygulama mağazaları) kullanabiliriz. Bağımsız veri sorumlusu olarak hareket ettikleri durumlarda
      (örneğin uygulama mağazası operatörü olarak Apple veya Google) kendi şartları ve gizlilik bildirimleri geçerlidir.
      Aktarım sırasında uygun olduğunda şifreli iletim (HTTPS gibi) kullanırız.
    </p>

    <h2>5. Bilgilerinizi Nasıl Kullanıyoruz</h2>
    <p>
      Topladığımız bilgileri Uygulamayı sağlamak ve geliştirmek, uygulanabildiğinde deneyiminizi kişiselleştirmek,
      güvenliği sağlamak, yasal yükümlülüklere uymak ve hizmet hakkında sizinle iletişim kurmak için kullanırız.
      Kişisel bilgilerinizi satmayız.
    </p>

    <h2>6. Saklama ve Silme</h2>
    <p>
      Bilgileri yalnızca yukarıdaki amaçlar için gerekli olduğu veya yasalarca gerektiği sürece saklarız.
      Hesabınızla ilişkili kişisel verilerin silinmesini (varsa) bizimle iletişime geçerek veya mevcutsa uygulama
      içi kontrolleri kullanarak talep edebilirsiniz.
    </p>

    <h2>7. Haklarınız</h2>
    <p>
      Bulunduğunuz yere bağlı olarak, kişisel verilerinize erişme, düzeltme, silme veya işlenmesini kısıtlama ya da
      belirli işlemlere itiraz etme hakkına sahip olabilirsiniz. Bu hakları kullanmak için bizimle iletişime geçin;
      makul bir süre içinde yanıt vereceğiz.
    </p>

    <h2>8. Güvenlik</h2>
    <p>
      Verileri korumak için makul teknik ve organizasyonel tedbirler uygularız. Hiçbir iletim veya depolama yöntemi
      tamamen güvenli değildir; kesin güvenliği garanti edemeyiz.
    </p>

    <h2>9. Uluslararası Transferler</h2>
    <p>
      Biz veya işlemcilerimiz verileri sınırlar arasında aktarırsa, gerektiğinde geçerli yasalarla uyumlu adımlar
      (uygun koruma tedbirleri veya sözleşme hükümleri gibi) atarız.
    </p>

    <h2>10. Bu Politikadaki Değişiklikler</h2>
    <p>
      Bu Politikayı zaman zaman güncelleyebiliriz. Önemli değişiklikler, güncellenmiş bir "Son Güncelleme" tarihi
      ve uygun olduğunda Uygulamada veya bu sayfada ek bir bildirimle yansıtılacaktır.
    </p>

    <h2>11. İletişim</h2>
    <p>Gizlilik soruları veya veri talepleri için iletişim:</p>
    <p>📧 <a className="l" href="mailto:info@onyxup.net">info@onyxup.net</a></p>
  </>
);

export const en = { privacyContent: privacyContentEn, termsContent: termsContentEn };
export const tr = { privacyContent: privacyContentTr, termsContent: termsContentTr };
